import React, { useState, useCallback } from 'react';
import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { Box, Link } from '@mui/material';

import BackgroundLayout from 'layouts/public-qr-code/background';

import { menuApi } from 'services/api';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import { fillRoute } from 'routes/hooks';

import { NotFoundError } from 'components/not-found-error';
import { HomepageBody } from 'components/qr-code-menu/homepage-body';
import { ClubFooter } from 'components/qr-code-menu/club-footer';
import { RefreshModal } from 'components/qr-code-menu/refresh-modal';

export default function QrCodeHomepage() {
  const [showRefreshPopup, setShowRefreshPopup] = useState(false);
  const [sortedMenuList, setSortedMenuList] = useState(undefined);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const showEmbedded = searchParams.get('embedded') == 'true';

  const params = useParams();
  const navigate = useNavigate();
  const { clubId, tableId } = params;

  const { isLoading, error, data } = useQuery(
    [`getMenusForTable_${clubId}_${tableId}`],
    async () => {
      const data = await menuApi.getMenusForTable(clubId, tableId);

      // Sorts menu by position
      const newSortedMenuList = data?.menu.sort((a, b) => a.position - b.position);

      // TODO: https://developer.mozilla.org/en-US/docs/Web/API/structuredClone or similar?
      // TODO: remove lodash if it's used only for this and replace it with https://github.com/FormidableLabs/react-fast-compare
      // IN THESE CASES WITHOUT MENU ORDER
      if (sortedMenuList !== undefined && !_.isEqual(sortedMenuList, newSortedMenuList)) {
        setShowRefreshPopup(true);
      }

      setSortedMenuList(newSortedMenuList);

      return data;
    },

    {
      refetchInterval: data => data?.refreshTime * 1000 ?? false,
      refetchIntervalInBackground: true,
      onError: null,
    },
  );

  // TODO: remove from the backend the footer field from this response since
  // there's no footer on this page

  const refreshMenu = () => {
    localStorage.removeItem('selectedLanguage');

    navigate(0);
  };

  const goToMenu = useCallback(
    menuId =>
      navigate(
        fillRoute(paths.qrcodeMenu.menu, { clubId, menuId, tableId }) + window.location.search,
      ),
    [clubId, tableId, navigate],
  );

  if (isLoading || !sortedMenuList) {
    return <BackgroundLayout isLoading />;
  }

  if (error) {
    if (error.status === 404) {
      return <NotFoundError />;
    }
    return <NotFoundError />;
  }

  return (
    <BackgroundLayout isLoading={isLoading} menuTheme={data.theme}>
      <RefreshModal open={showRefreshPopup} onClose={refreshMenu} />

      {!showEmbedded && (
        <Link
          onClick={() => navigate(0)}
          aria-label="go to homepage"
          sx={{
            lineHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 350,
              lineHeight: 0,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pt: 2,
            }}
          >
            <img src={data.logo} alt="logo" style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>
        </Link>
      )}

      <Box
        sx={{
          flexGrow: 1,
        }}
      >
        <HomepageBody
          items={sortedMenuList}
          onClickViewMenu={goToMenu}
          colorTheme={data.theme}
          translationsEnabled={data.translationsEnabled}
        />
      </Box>

      {!showEmbedded && (
        <ClubFooter
          address={data.clubAddress}
          phone={data.clubDisplayPhone}
          email={data.clubDisplayEmail}
          instagram={data.clubInstagram}
          facebook={data.clubFacebook}
          website={data.clubWebsite}
          whatsapp={data.clubWhatsapp}
        />
      )}
    </BackgroundLayout>
  );
}
